import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Save, DollarSign, TrendingUp, Ticket, Users2, GraduationCap, Briefcase } from "lucide-react";

const IMPACT_FIELDS = [
  { key: "sponsorship_goal", label: "Sponsorship Goal" },
  { key: "sponsorship_raised", label: "Sponsorship Raised" },
  { key: "scholarship_goal", label: "Scholarship Goal" },
  { key: "scholarship_raised", label: "Scholarship Raised" },
  { key: "partnership_goal", label: "Strategic Partnership Goal" },
  { key: "partnership_raised", label: "Strategic Partnership Raised" },
  { key: "donations_goal", label: "Donations Goal" },
];

const METRICS_FIELDS = [
  { key: "tickets_sold", label: "Tickets Sold", icon: Ticket },
  { key: "total_capacity", label: "Total Capacity", icon: Ticket },
  { key: "vip_tables_available", label: "VIP Tables Available", icon: Users2 },
  { key: "vip_tables_reserved", label: "VIP Tables Reserved", icon: Users2 },
  { key: "sponsors_secured", label: "Sponsors Secured", icon: TrendingUp },
  { key: "scholarships_funded", label: "Scholarships Funded", icon: GraduationCap },
  { key: "workforce_participants", label: "Workforce Participants Served", icon: Users2 },
  { key: "scholarships_awarded", label: "Scholarships Awarded (Cumulative)", icon: GraduationCap },
  { key: "industry_partnerships", label: "Industry Partnerships", icon: Briefcase },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [impact, setImpact] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [i, m] = await Promise.all([api.get("/impact"), api.get("/metrics")]);
    setImpact(i.data);
    setMetrics(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setImpactField = (k, v) => setImpact({ ...impact, [k]: Number(v) || 0 });
  const setMetricField = (k, v) => setMetrics({ ...metrics, [k]: Number(v) || 0 });

  const saveAll = async () => {
    setSaving(true);
    try {
      const impactBody = Object.fromEntries(IMPACT_FIELDS.map((f) => [f.key, impact[f.key]]));
      const metricsBody = Object.fromEntries(METRICS_FIELDS.map((f) => [f.key, metrics[f.key]]));
      await Promise.all([
        api.patch("/admin/impact", impactBody, { withCredentials: true }),
        api.patch("/admin/metrics", metricsBody, { withCredentials: true }),
      ]);
      toast.success("All metrics published to the public site.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!impact || !metrics) return <p className="text-white/60">Loading…</p>;

  const totalRaised = impact.sponsorship_raised + impact.scholarship_raised
    + impact.partnership_raised + (impact.donations_raised || 0)
    + (impact.in_kind_total || 0);
  const totalGoal = impact.sponsorship_goal + impact.scholarship_goal
    + impact.partnership_goal + (impact.donations_goal || 0);

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">DASHBOARD</p>
          <h1 className="font-display text-4xl text-white">Welcome back, {user?.name?.split(" ")[0] || "Admin"}.</h1>
          <p className="text-white/50 mt-2 text-sm">Every change saves to the public site immediately.</p>
        </div>
        {editable && (
          <button data-testid="save-all-btn" onClick={saveAll} disabled={saving} className="btn-pink disabled:opacity-50">
            <Save size={16} /> {saving ? "SAVING…" : "SAVE & PUBLISH"}
          </button>
        )}
      </div>

      {/* Snapshot cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SnapshotCard label="Total Raised" value={`$${totalRaised.toLocaleString()}`} sub={`of $${totalGoal.toLocaleString()}`} accent />
        <SnapshotCard label="Tickets Sold" value={metrics.tickets_sold} sub={`${metrics.seats_remaining} seats remaining`} />
        <SnapshotCard label="VIP Tables Left" value={metrics.vip_tables_remaining} sub={`of ${metrics.vip_tables_available}`} />
        <SnapshotCard label="Sponsors Secured" value={metrics.sponsors_secured} sub={`${metrics.scholarships_funded} scholarships funded`} />
      </div>

      {/* Fundraising Progress */}
      <Section title="Fundraising Progress" subtitle="Live impact counter on the homepage hero dashboard.">
        <div className="grid md:grid-cols-2 gap-4">
          {IMPACT_FIELDS.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              value={impact[f.key]}
              onChange={(v) => setImpactField(f.key, v)}
              readOnly={!editable}
              testId={`impact-${f.key}`}
              prefix={<DollarSign size={14} className="text-aawta-pink" />}
            />
          ))}
        </div>
      </Section>

      {/* Capacity + Community Impact */}
      <Section title="Capacity & Community Impact" subtitle="Public-facing event capacity + cumulative community-impact stats.">
        <div className="grid md:grid-cols-3 gap-4">
          {METRICS_FIELDS.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              value={metrics[f.key]}
              onChange={(v) => setMetricField(f.key, v)}
              readOnly={!editable}
              testId={`metric-${f.key}`}
              prefix={<f.icon size={14} className="text-aawta-pink" />}
            />
          ))}
        </div>
      </Section>
    </div>
  );
}

function SnapshotCard({ label, value, sub, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-aawta-pink/40 bg-aawta-pink/5" : "border-white/10 bg-aawta-charcoal/40"}`}>
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-3xl text-white mt-2">{value}</p>
      <p className="text-xs text-white/40 mt-1">{sub}</p>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="font-display text-2xl text-white">{title}</h2>
        <p className="text-white/50 text-sm">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, readOnly, testId, prefix }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 flex items-center gap-2">{prefix}{label}</span>
      <input
        data-testid={testId}
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        className="w-full bg-aawta-charcoal/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm font-mono transition-colors disabled:opacity-60"
      />
    </label>
  );
}
